import React from 'react'
import {
    Box,
    Checkbox,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { ConversionTriggerRule } from './transitionConfig.types'
import { labelForConversionTrigger } from './conversionRulesUtils'
import { PM_MANUAL_TRIGGER } from './transitionTriggerCatalog'
import { LEAD_CONVERSION_TRIGGER_OPTIONS } from './stepConfig.types'

const useStyles = makeStyles((theme: Theme) => ({
    ruleRow: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
        padding: theme.spacing(1, 1.5, 1.25),
        marginBottom: theme.spacing(1),
    },
    ruleHeader: {
        display: 'flex',
        alignItems: 'flex-start',
    },
    ruleMeta: {
        marginLeft: theme.spacing(4),
        marginTop: theme.spacing(0.5),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
    },
    ruleHelper: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
        marginLeft: theme.spacing(4),
    },
    approvalLabel: {
        fontSize: '0.8125rem',
        color: theme.palette.text.secondary,
    },
}))

interface ProspectsToLeadsEdgeFieldsProps {
    rules: ConversionTriggerRule[]
    reviewPrompt: string
    readOnly?: boolean
    onRulesChange: (rules: ConversionTriggerRule[]) => void
    onReviewPromptChange: (value: string) => void
}

export const ProspectsToLeadsEdgeFields: React.FC<ProspectsToLeadsEdgeFieldsProps> = ({
    rules,
    reviewPrompt,
    readOnly = false,
    onRulesChange,
    onReviewPromptChange,
}) => {
    const classes = useStyles()
    const showReviewPrompt = rules.some(
        (rule) =>
            rule.enabled &&
            rule.require_pm_approval &&
            rule.trigger !== PM_MANUAL_TRIGGER,
    )

    const updateRule = (
        trigger: string,
        patch: Partial<ConversionTriggerRule>,
    ) => {
        onRulesChange(
            rules.map((rule) =>
                rule.trigger === trigger ? { ...rule, ...patch } : rule,
            ),
        )
    }

    return (
        <>
            <Typography variant="subtitle2" gutterBottom>
                Convert to lead when
            </Typography>
            <Typography variant="body2" color="textSecondary" paragraph>
                A prospect becomes a lead when any enabled event occurs.
            </Typography>

            {LEAD_CONVERSION_TRIGGER_OPTIONS.map((option) => {
                const rule =
                    rules.find((item) => item.trigger === option.value) ??
                    ({
                        trigger: option.value,
                        enabled: false,
                        require_pm_approval: false,
                    } as ConversionTriggerRule)
                const isManual = option.value === PM_MANUAL_TRIGGER

                return (
                    <Box key={option.value} className={classes.ruleRow}>
                        <FormControlLabel
                            className={classes.ruleHeader}
                            control={
                                <Checkbox
                                    checked={rule.enabled}
                                    onChange={(_, checked) =>
                                        updateRule(option.value, {
                                            enabled: checked,
                                        })
                                    }
                                    disabled={readOnly}
                                    color="primary"
                                />
                            }
                            label={option.label}
                        />

                        {isManual ? (
                            <Typography className={classes.ruleHelper}>
                                PM acts directly — no approval needed.
                            </Typography>
                        ) : (
                            rule.enabled && (
                                <Box className={classes.ruleMeta}>
                                    <Typography
                                        className={classes.approvalLabel}
                                    >
                                        PM approval required
                                    </Typography>
                                    <Switch
                                        checked={rule.require_pm_approval}
                                        onChange={(_, checked) =>
                                            updateRule(option.value, {
                                                require_pm_approval: checked,
                                            })
                                        }
                                        disabled={readOnly}
                                        color="primary"
                                        size="small"
                                    />
                                </Box>
                            )
                        )}
                    </Box>
                )
            })}

            {showReviewPrompt && (
                <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Review prompt"
                    value={reviewPrompt}
                    disabled={readOnly}
                    onChange={(e) => onReviewPromptChange(e.target.value)}
                    helperText="Shown to the PM when a conversion event requires approval."
                    style={{ marginTop: 8 }}
                />
            )}

            <Typography
                variant="caption"
                color="textSecondary"
                display="block"
                style={{ marginTop: 12 }}
            >
                Summary:{' '}
                {rules
                    .filter((rule) => rule.enabled)
                    .map((rule) => {
                        if (
                            rule.require_pm_approval &&
                            rule.trigger !== PM_MANUAL_TRIGGER
                        ) {
                            return `${labelForConversionTrigger(
                                rule.trigger,
                            )} (PM approves)`
                        }
                        return labelForConversionTrigger(rule.trigger)
                    })
                    .join(' · ') || 'No triggers enabled'}
            </Typography>
        </>
    )
}
